import { useRecordContext } from "react-admin";
import { AddVehicleButton } from "../../buttons/AddVehicleButton";
import { PageTitle } from "../../misc/PageTitle";
import { Typography } from "@mui/material";
import { Row } from "../../misc/Row";
import { CarIcon } from "../../icons/CarIcon";
import { MotorcycleIcon } from "../../icons/MotorcycleIcon";
import { TrailerIcon } from "../../icons/TrailerIcon";
import { SimulatorIcon } from "../../icons/SimulatorIcon";
import { useAutovioContext } from "../../hooks/useAutovioContext";
import { useLocation } from "react-router-dom";
import { CarsList } from "../vehicles/CarsList";
import { MotorcyclesList } from "../vehicles/MotorcyclesList";
import { TrailersList } from "../vehicles/TrailersList";
import { SimulatorsList } from "../vehicles/SimulatorsList";
import { DeletedVehiclesList } from "../vehicles/DeletedVehiclesList";
import { grants } from "../../backoffice.access_control";
import { DrivingSchool } from "../../providers/drivingSchoolsProvider";
import { TruckIcon } from "../../icons/TruckIcon";
import { TrucksList } from "../vehicles/TrucksList";
import { BusIcon } from "../../icons/BusIcon";
import { TractorIcon } from "../../icons/TractorIcon";
import { BusesList } from "../vehicles/BusesList";
import { TractorsList } from "../vehicles/TractorsList";
import { Column } from "../../misc/Column";
import Box from "@mui/material/Box";
import { getSelectedResponsiveHashTab, ResponsiveHashTabs } from "../../misc/ResponsiveHashTabs";

export function DrivingSchoolFleet() {
  const [{ drivingSchoolId }] = useAutovioContext();
  const drivingSchool = useRecordContext<DrivingSchool>();
  const { hash } = useLocation();

  if (!drivingSchoolId) {
    return (
      <Box className="centered">
        <Column sx={{ alignItems: "center" }}>Bitte wähle eine Fahrschule aus.</Column>
      </Box>
    );
  }

  if (!drivingSchool || drivingSchoolId !== drivingSchool.id) {
    return null;
  }

  const trucks = drivingSchool.customizations.includes("trucks");
  const buses = drivingSchool.customizations.includes("buses");
  const tractors = drivingSchool.customizations.includes("tractors");
  const tabs = [
    {
      value: "cars",
      mobileLabel: trucks ? "PKWs" : "Autos",
      label: (
        <Row spacing={1} sx={{ alignItems: "center" }}>
          <CarIcon />
          <Typography variant="body2">{trucks ? "PKWs" : "Autos"}</Typography>
        </Row>
      ),
      content: (
        <>
          <CarsList />
          {grants.has("Vehicle:viewDeleted") && (
            <DeletedVehiclesList title={`Inaktive ${trucks ? "PKWs" : "Autos"}`} resource="cars" />
          )}
        </>
      ),
      addVehicleButton: <AddVehicleButton type="car" data-testid="add-car-button" />,
    },
    {
      value: "motorcycles",
      mobileLabel: "Motorräder",
      label: (
        <Row spacing={1} sx={{ alignItems: "center" }}>
          <MotorcycleIcon />
          <Typography variant="body2">Motorräder</Typography>
        </Row>
      ),
      content: (
        <>
          <MotorcyclesList />
          {grants.has("Vehicle:viewDeleted") && (
            <DeletedVehiclesList title="Inaktive Motorräder" resource="motorcycles" />
          )}
        </>
      ),
      addVehicleButton: <AddVehicleButton type="motorcycle" data-testid="add-motorcycle-button" />,
    },
    {
      value: "trailers",
      mobileLabel: "Anhänger",
      label: (
        <Row spacing={1} sx={{ alignItems: "center" }}>
          <TrailerIcon />
          <Typography variant="body2">Anhänger</Typography>
        </Row>
      ),
      content: (
        <>
          <TrailersList />
          {grants.has("Vehicle:viewDeleted") && <DeletedVehiclesList title="Inaktive Anhänger" resource="trailers" />}
        </>
      ),
      addVehicleButton: <AddVehicleButton type="trailer" data-testid="add-trailer-button" />,
    },
    {
      value: "simulators",
      mobileLabel: "Simulatoren",
      label: (
        <Row spacing={1} sx={{ alignItems: "center" }}>
          <SimulatorIcon />
          <Typography variant="body2">Simulatoren</Typography>
        </Row>
      ),
      content: (
        <>
          <SimulatorsList />
          {grants.has("Vehicle:viewDeleted") && (
            <DeletedVehiclesList title="Inaktive Simulatoren" resource="simulators" />
          )}
        </>
      ),
      addVehicleButton: <AddVehicleButton type="simulator" data-testid="add-simulator-button" />,
    },
    trucks && {
      value: "trucks",
      mobileLabel: "LKWs",
      label: (
        <Row spacing={1} sx={{ alignItems: "center" }}>
          <TruckIcon />
          <Typography variant="body2">LKWs</Typography>
        </Row>
      ),
      content: (
        <>
          <TrucksList />
          {grants.has("Vehicle:viewDeleted") && <DeletedVehiclesList title="Inaktive LKWs" resource="trucks" />}
        </>
      ),
      addVehicleButton: <AddVehicleButton type="truck" data-testid="add-truck-button" />,
    },
    buses && {
      value: "buses",
      mobileLabel: "Busse",
      label: (
        <Row spacing={1} sx={{ alignItems: "center" }}>
          <BusIcon />
          <Typography variant="body2">Busse</Typography>
        </Row>
      ),
      content: (
        <>
          <BusesList />
          {grants.has("Vehicle:viewDeleted") && <DeletedVehiclesList title="Inaktive Busse" resource="buses" />}
        </>
      ),
      addVehicleButton: <AddVehicleButton type="bus" data-testid="add-bus-button" />,
    },
    tractors && {
      value: "tractors",
      mobileLabel: "Traktoren",
      label: (
        <Row spacing={1} sx={{ alignItems: "center" }}>
          <TractorIcon />
          <Typography variant="body2">Traktoren</Typography>
        </Row>
      ),
      content: (
        <>
          <TractorsList />
          {grants.has("Vehicle:viewDeleted") && <DeletedVehiclesList title="Inaktive Traktoren" resource="tractors" />}
        </>
      ),
      addVehicleButton: <AddVehicleButton type="tractor" data-testid="add-tractor-button" />,
    },
  ].filter((tab) => tab !== false);
  const selectedTab = getSelectedResponsiveHashTab(tabs, hash);

  return (
    <div style={{ position: "relative" /* ... for positioning the (+ Hinzufügen) button */ }}>
      <Row justifyContent="space-between" alignItems="center" sx={{ mb: "30px", minHeight: "32px" }}>
        <PageTitle documentTitle="Fuhrpark">
          <h2 style={{ margin: 0, lineHeight: 0.83 }}>Fuhrpark</h2>
        </PageTitle>
        {grants.has("Vehicle:create") ? selectedTab.addVehicleButton : undefined}
      </Row>
      <ResponsiveHashTabs tabs={tabs} mobileSelectAriaLabel="Fuhrpark-Ansicht" className="driving-school-fleet-tabs" />
    </div>
  );
}
